<template>
  <div class="options-container">
    <select
      class="singular-select"
      name="id"
      :value="questionRef.id || ''"
      @change="handleQuestionChange"
    >
      <option value="" disabled>{{ parseTranslation(QUESTION) }}</option>

      <option
        v-for="question in available_questions"
        :key="`${id}-${question.qid}`"
        :value="question.qid"
      >
        {{ question.code }}
      </option>
    </select>

    <select
      class="singular-select"
      v-if="referenceSubQuestions.length"
      name="subQuestion"
      :key="`${id}-sub-${getValue('subQuestion')}`"
      :value="getValue('subQuestion')"
      @change="handleSelection"
    >
      <option value="" disabled>{{ '' }}</option>

      <option
        v-for="(subQuestion, index) in referencedQuestion.subQuestions"
        :key="index"
        :value="`${subQuestion.code}`"
      >
        {{ subQuestion.code }}
      </option>
    </select>

    <select
      class="singular-select"
      v-if="referenceAnswers.length"
      name="answer"
      :key="`${id}-sub-${getValue('answer')}`"
      :value="getValue('answer')"
      @change="handleSelection"
    >
      <option value="" disabled>{{ '' }}</option>

      <option
        v-for="(answer, index) in referencedQuestion.answers"
        :key="index"
        :value="`${answer.code}`"
      >
        {{ answer.code }}
      </option>
    </select>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { questionsConfig } from '@/utils/questions'
import { removePropertiesFromObject, removeUndefinedProperties } from '@/utils/object'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'AlternativesType',
  components: {},

  data() {
    return {
      translations,
      ...translationKeys,
      collapsePanel: false,
    }
  },

  props: {
    id: {
      required: true,
      type: String,
    },
    available_questions: {
      required: true,
      type: Array,
    },
    condition: {
      required: true,
      type: Object,
    },
    update_condition: {
      required: true,
      type: Function,
    },
    remove_self: {
      required: true,
      type: Function,
    },
  },

  computed: {
    questionRef() {
      return this.condition.questionRef
    },

    referencedQuestion() {
      const referenced = this.getQuestionById(this.questionRef.id)

      return referenced
    },

    referenceQuestionCode() {
      return this.referencedQuestion?.code
    },

    referenceType() {
      return this.referencedQuestion?.type
    },

    referenceSubQuestions() {
      return this.referencedQuestion?.subQuestions || []
    },

    referenceAnswers() {
      return this.referencedQuestion?.answers || []
    },
  },

  watch: {
    referenceQuestionCode() {
      this.validateCodeReference()
    },

    referenceSubQuestions() {
      const shouldExcludeProp = !questionsConfig[this.referenceType]?.hasSubQuestion

      this.validateAlternativesReference(
        'subQuestion',
        this.referenceSubQuestions,
        shouldExcludeProp,
      )
    },

    referenceAnswers() {
      const shouldExcludeProp = !questionsConfig[this.referenceType]?.hasAnswer

      this.validateAlternativesReference(
        'answer',
        this.referenceAnswers,
        shouldExcludeProp,
      )
    },
  },

  methods: {
    ...mapActions(['sendMessage']),

    getValue(prop) {
      return this.questionRef[prop] || ''
    },

    validateCodeReference() {
      if (!this.referenceQuestionCode) return this.remove_self()

      if (this.referenceQuestionCode === this.questionRef.code) return

      const updatedCondition = {
        ...this.condition,
        questionRef: {
          ...this.condition.questionRef,
          code: this.referenceQuestionCode,
        },
      }

      this.update_condition(updatedCondition)
    },

    excludeRefProperty(property) {
      const updatedRef = removePropertiesFromObject(this.questionRef, property)

      const updatedCondition = {
        ...this.condition,
        questionRef: updatedRef,
      }

      this.update_condition(updatedCondition)
    },

    validateAlternativesReference(referenceProp, alternatives, shouldExcludeProp) {
      if (!this.referencedQuestion) return

      const existingReference = this.questionRef[referenceProp]

      if (!existingReference) return

      if (shouldExcludeProp) return this.excludeRefProperty(referenceProp)

      const referenceIsValid = alternatives.find(
        ({ code }) => `${code}` === `${existingReference}`,
      )

      if (!referenceIsValid) this.remove_self()
    },

    handleSelection({ target }) {
      const { value, name } = target

      const updatedCondition = {
        ...this.condition,
        questionRef: {
          ...this.condition.questionRef,
          [name]: value,
        },
      }

      this.update_condition(updatedCondition)
    },

    getQuestionById(id) {
      const referenced = this.available_questions.find(({ qid }) => qid === id)

      return referenced
    },

    getFirstAlternativeCode(alternatives) {
      if (!alternatives.length) return

      const [{ code }] = alternatives

      return `${code}`
    },

    handleQuestionChange({ target }) {
      const { value, name } = target

      const newQuestion = this.getQuestionById(value)

      const [subQuestion, answer] = ['subQuestions', 'answers'].map(prop =>
        this.getFirstAlternativeCode(newQuestion[prop]),
      )

      const updatedCondition = {
        ...this.condition,
        questionRef: removeUndefinedProperties({
          [name]: value,
          subQuestion,
          answer,
        }),
      }

      this.update_condition(updatedCondition)
    },

    created() {
      this.validateCodeReference()
      this.validateAlternativesReference('subQuestion', this.referenceSubQuestions)
      this.validateAlternativesReference('answer', this.referenceAnswers)
    },
  },
}
</script>

<style scoped lang="scss">
.options-container {
  @include flexAlignCenter;
}

select {
  @include baseSelect;
  margin-left: 6px;

  option {
    color: $gray-2;
  }
}
</style>
